/** Display name + marketing opt-in handlers for account settings. */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/features/auth/useAuth';
import { useTier } from '@/features/tier/useTier';
import { persistUserDisplayName } from '@/services/authService';
import { setUserMarketingOptIn } from '@/services/userService';
import { MARKETING_OPT_IN_SOURCE_SETTINGS } from '@/constants/marketingOptIn';
import { validateOnboardingDisplayName } from '@/features/onboarding/utils/onboardingDisplayNameUtils';

/**
 * @returns {{
 *   canEdit: boolean,
 *   displayName: string,
 *   setDisplayName: (value: string) => void,
 *   displayNameErrorKey: string | null,
 *   displayNameSaving: boolean,
 *   saveDisplayName: () => Promise<void>,
 *   marketingOptIn: boolean,
 *   marketingSaving: boolean,
 *   marketingErrorKey: string | null,
 *   toggleMarketingOptIn: (next: boolean) => Promise<void>,
 * }}
 */
export function useAccountSettings() {
  const { user } = useAuth();
  const { profileDisplayName, marketingOptInGranted } = useTier();

  const canEdit = Boolean(user?.uid && !user?.isAnonymous);

  const committedName = profileDisplayName || user?.displayName || '';

  const [displayName, setDisplayName] = useState(committedName);
  const [displayNameErrorKey, setDisplayNameErrorKey] = useState(null);
  const [displayNameSaving, setDisplayNameSaving] = useState(false);
  const displayNameSaveInFlight = useRef(false);

  const [marketingOptIn, setMarketingOptIn] = useState(marketingOptInGranted);
  const [marketingSaving, setMarketingSaving] = useState(false);
  const [marketingErrorKey, setMarketingErrorKey] = useState(null);
  const marketingSaveInFlight = useRef(false);

  useEffect(() => {
    setDisplayName(committedName);
  }, [committedName]);

  useEffect(() => {
    setMarketingOptIn(marketingOptInGranted);
  }, [marketingOptInGranted]);

  const saveDisplayName = useCallback(async () => {
    if (!canEdit || displayNameSaveInFlight.current) {
      return;
    }
    const validation = validateOnboardingDisplayName(displayName);
    if (!validation.ok) {
      setDisplayNameErrorKey(
        validation.reason === 'tooLong'
          ? 'settings.account.displayName.errorTooLong'
          : 'settings.account.displayName.errorEmpty',
      );
      return;
    }
    if (validation.value === committedName.trim()) {
      setDisplayNameErrorKey(null);
      return;
    }
    displayNameSaveInFlight.current = true;
    setDisplayNameSaving(true);
    setDisplayNameErrorKey(null);
    const previous = committedName;
    try {
      await persistUserDisplayName(validation.value);
    } catch {
      setDisplayName(previous);
      setDisplayNameErrorKey('settings.account.displayName.errorSave');
    } finally {
      displayNameSaveInFlight.current = false;
      setDisplayNameSaving(false);
    }
  }, [canEdit, displayName, committedName]);

  const toggleMarketingOptIn = useCallback(
    async (next) => {
      if (!canEdit || !user?.uid || marketingSaveInFlight.current) {
        return;
      }
      if (next === marketingOptInGranted) {
        return;
      }
      marketingSaveInFlight.current = true;
      setMarketingSaving(true);
      setMarketingErrorKey(null);
      const previous = marketingOptInGranted;
      setMarketingOptIn(next);
      try {
        await setUserMarketingOptIn(user.uid, {
          optedIn: next,
          source: MARKETING_OPT_IN_SOURCE_SETTINGS,
        });
      } catch {
        setMarketingOptIn(previous);
        setMarketingErrorKey('settings.account.marketing.errorSave');
      } finally {
        marketingSaveInFlight.current = false;
        setMarketingSaving(false);
      }
    },
    [canEdit, user?.uid, marketingOptInGranted],
  );

  return {
    canEdit,
    displayName,
    setDisplayName,
    displayNameErrorKey,
    displayNameSaving,
    saveDisplayName,
    marketingOptIn,
    marketingSaving,
    marketingErrorKey,
    toggleMarketingOptIn,
  };
}
